/**
 * Format booking ID to short reference for display
 * Example: "a55f9bc0-0257-405a-9ac6-1a12a529f045" -> "#a55f9bc0"
 */
export const formatBookingReference = (bookingId) => {
  if (!bookingId) return 'N/A';
  const shortId = bookingId.split('-')[0];
  return `#${shortId}`;
};

/**
 * Format payment method for display
 */
export const formatPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) return 'Pending';
  
  const methodMap = {
    'pocket_pay_card': 'Card',
    'card': 'Card',
    'pocket_pay_qr': 'QR Code',
    'pocket_pay_biller': 'Bank Transfer',
    'pocket_qr': 'Pocket QR',
    'cash': 'Cash',
    'online_banking': 'Online Banking',
    'wallet': 'E-Wallet',
    'pay_later': 'Pay Later',
    'bank_transfer': 'Bank Transfer',
    'complimentary': 'Complimentary',
    // raw Pocket Pay strings (legacy / direct from API)
    'pocket qr': 'QR Code',
    'biller': 'Bank Transfer',
  };
  
  return methodMap[paymentMethod] || methodMap[paymentMethod?.toLowerCase()] || paymentMethod;
};

/**
 * Get payment method badge color
 */
export const getPaymentMethodColor = (paymentMethod) => {
  const colorMap = {
    'pocket_pay_card': 'blue',
    'card': 'blue',
    'pocket_pay_qr': 'purple',
    'pocket_pay_biller': 'green',
    'pocket_qr': 'purple',
    'cash': 'green',
    'online_banking': 'purple',
    'wallet': 'orange',
    'pay_later': 'orange',
    'bank_transfer': 'green',
    'complimentary': 'purple',
    'pocket qr': 'purple',
    'biller': 'green',
  };
  
  return colorMap[paymentMethod] || colorMap[paymentMethod?.toLowerCase()] || 'gray';
};

/**
 * Canonical "is this a manual staff Pay Later hold" check — single source
 * of truth shared by the Bookings-list status badge AND the Dashboard
 * calendar cell styling so the two surfaces can never drift apart again.
 * A Pay Later hold is AWAITING_PAYMENT under the hood (same self-serve
 * Pocket Pay "Confirm & Pay" flow as any other hold) but flagged
 * `no_auto_expiry` so the sweeper never auto-cancels it.
 */
export const isManualPayLaterHold = (booking) => booking?.status === 'AWAITING_PAYMENT'
  && (booking?.source || '').toUpperCase() === 'MANUAL_STAFF'
  && booking?.no_auto_expiry === true;

/**
 * Booking status label for display. Manual staff "Pay Later" bookings are
 * AWAITING_PAYMENT under the hood (so they get the same self-serve Pocket
 * Pay "Confirm & Pay" flow as any other awaiting-payment booking) but never
 * auto-expire — shown as "Reserved – Pay Later" instead of the generic
 * "Awaiting Payment" so customers/staff don't mistake it for a ticking hold.
 */
export const getBookingStatusLabel = (booking) => {
  const payLaterHold = isManualPayLaterHold(booking);
  if (payLaterHold && booking?.payment_status !== 'paid') {
    return 'Reserved – Pay Later';
  }
  // Manual pay-later marked paid via the admin "Mark Paid" action doesn't
  // flip `status` (mark-paid only updates payment_status) — without this,
  // the raw status would misleadingly still read "AWAITING PAYMENT" even
  // though it's fully paid.
  if (payLaterHold && booking?.payment_status === 'paid') {
    return 'Confirmed';
  }
  return (booking?.status || '').replace(/_/g, ' ');
};
